import { ReactElement, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiBreadcrumbs, { BreadcrumbsProps } from '@mui/material/Breadcrumbs';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import MainCard from 'components/MainCard';
import navigation from 'menu-items';
import { ThemeDirection } from 'config';
import RecordLimitWarning from 'components/controls/RecordLimitWarning';

// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';

// types
import { OverrideIcon } from 'types/root';
import { NavItemType } from 'types/menu';

import { Tooltip, IconButton } from '@mui/material';

import { matchPath, useParams } from 'react-router-dom';
import { useLoginLogs } from 'contexts/LoginLogsContext';
import { useEmailLogs } from 'contexts/EmailLogsContext';
import { usePageHeader } from 'contexts/PageHeaderContext';

interface BreadcrumbLinkProps {
  title: string;
  subtitle?: string;
  to?: string;
  icon?: string | OverrideIcon;
}

// ==============================|| BREADCRUMBS ||============================== //

interface Props {
  card?: boolean;
  custom?: boolean;
  divider?: boolean;
  heading?: string | ReactElement;
  subHeading?: string;
  icon?: boolean;
  icons?: boolean;
  links?: BreadcrumbLinkProps[];
  maxItems?: number;
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadcrumbsProps['sx'];
}

export default function Breadcrumbs({
  card = false,
  custom = false,
  divider = false,
  heading,
  subHeading,
  icon,
  icons,
  links,
  maxItems,
  rightAlign,
  separator,
  title = true,
  titleBottom = true,
  sx,
  ...others
}: Props) {
  const theme = useTheme();
  const location = useLocation();
  const { headerAction, recordLimitInfo } = usePageHeader();

  const path = window.location.pathname;
  const tabname = path.split('/')[1] == 'customers' ? path.split('/')[3] : '';

  const [main, setMain] = useState<NavItemType | undefined>();
  const [item, setItem] = useState<NavItemType>();



  const { totalRecords: loginTotalRecords } = useLoginLogs();
  const { totalRecords: emailTotalRecords } = useEmailLogs();

  let totalRecords;
  if (window.location.pathname === '/apps/loginLogs') {
    totalRecords = loginTotalRecords;
  } else if (window.location.pathname === '/apps/emailLogs' || window.location.pathname === '/apps/emaillogs') {
    totalRecords = emailTotalRecords;
  }


  const iconSX = {
    marginRight: theme.direction === ThemeDirection.RTL ? 0 : theme.spacing(0.75),
    marginLeft: theme.direction === ThemeDirection.RTL ? theme.spacing(0.75) : 0,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };

  let customLocation = location.pathname;

  // only used for component demo breadcrumbs
  if (customLocation.includes('/components-overview/breadcrumbs')) {
    customLocation = '/apps/customer/customer-card';
  }

  useEffect(() => {
    navigation?.items?.map((menu: NavItemType) => {
      if (menu.type && menu.type === 'group') {
        if (menu?.url && menu.url === customLocation) {
          setMain(menu);
          setItem(menu);
        } else {
          getCollapse(menu as { children: NavItemType[]; type?: string });
        }
      }
      return false;
    });
  });

  // set active item state
  const getCollapse = (menu: NavItemType) => {
    if (!custom && menu.children) {
      menu.children.filter((collapse: NavItemType) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse as { children: NavItemType[]; type?: string });
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type && collapse.type === 'item') {
          if (matchPath({ path: String(collapse.url), end: true }, customLocation)) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  // item separator
  const SeparatorIcon = separator!;
  const separatorIcon = separator ? <SeparatorIcon style={{ fontSize: '0.75rem', marginTop: 2 }} /> : '/';

  let mainContent;
  let itemContent;
  let breadcrumbContent: ReactElement = <Typography />;
  let itemTitle: NavItemType['title'] = '';
  let itemSubtitle: NavItemType['subtitle'] = '';
  let CollapseIcon;
  let ItemIcon;

  // collapse item
  if (main && (main.type === 'collapse' || main.type === 'group') && !main.breadcrumbs) {
    if (main.title !== item?.title) {
      CollapseIcon = main.icon ? main.icon : ApartmentOutlined;
      mainContent = (
        <Typography
          {...(main.url && { component: Link, to: main.url as string })}
          variant={window.location.pathname === main.url ? 'subtitle1' : 'h6'}
          sx={{ textDecoration: 'none' }}
          color={window.location.pathname === main.url ? 'text.primary' : 'text.secondary'}
        >
          {icons && <CollapseIcon style={iconSX} />}
          <FormattedMessage id={main?.title as string} />
        </Typography>
      );
    }

    if (!!custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
          {...others}
          content={card}
          shadow="none"
        >
          <Grid
            container
            direction={rightAlign ? 'row' : 'column'}
            justifyContent={rightAlign ? 'space-between' : 'flex-start'}
            alignItems={rightAlign ? 'center' : 'flex-start'}
            spacing={1}
          >
            <Grid>
              <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
                {mainContent}
              </MuiBreadcrumbs>
            </Grid>
            {title && titleBottom && (
              <Grid>
                <Typography variant="h2">
                  <FormattedMessage id={main.title as string} />
                </Typography>
              </Grid>
            )}
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  // items
  if ((item && item.type === 'item') || (item?.type === 'group' && item?.url) || custom) {
    itemTitle = item?.title;
    itemSubtitle = item?.subtitle;
    ItemIcon = item?.icon ? item.icon : ApartmentOutlined;
    itemContent = (
      <Typography variant="subtitle1" color="text.primary">
        {icons && <ItemIcon style={iconSX} />}
        <FormattedMessage id={itemTitle as string} />
      </Typography>
    );

    let tempContent = (
      <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
        {mainContent}
        {itemContent}
        {tabname && (
          <Typography variant="subtitle1" color="text.primary">
            {tabname == 'drivers-license'
              ? `Driver's License`
              : tabname
                .split(/[-_]/)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
          </Typography>
        )}
      </MuiBreadcrumbs>
    );

    if (custom && links && links?.length > 0) {
      tempContent = (
        <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
          {links?.map((link: BreadcrumbLinkProps, index: number) => {
            CollapseIcon = link.icon ? link.icon : ApartmentOutlined;

            return (
              <Typography
                key={index}
                {...(link.to && { component: Link, to: link.to })}
                variant={!link.to ? 'subtitle1' : 'h6'}
                sx={{ textDecoration: 'none' }}
                color={!link.to ? 'text.primary' : 'text.secondary'}
              >
                {link.icon && <CollapseIcon style={iconSX} />}
                <FormattedMessage id={link.title as string} />
              </Typography>
            );
          })}
        </MuiBreadcrumbs>
      );
    }

    // main
    if (item?.breadcrumbs !== false || custom) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={card === false ? { mb: 3, bgcolor: 'inherit', backgroundImage: 'none', ...sx } : { mb: 3, ...sx }}
          {...others}
          content={card}
          shadow="none"
        >
          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            {title && (
              <Grid>
                <Typography variant="h2">
                  {(() => {
                    if (custom && heading && typeof heading !== 'string') {
                      return heading;
                    }

                    const currentTitle = (custom ? heading : (item?.title as string)) || '';
                    if (typeof currentTitle !== 'string') return currentTitle;

                    const words = currentTitle.split(' ');
                    const firstToken = currentTitle.split(' ')[0];

                    const hashIndex = words.findIndex(w => w.startsWith('#'));

                    const beforeHash = hashIndex !== -1 ? words.slice(1, hashIndex).join(' ') : words.slice(1).join(' ');
                    const hashPart = hashIndex !== -1 ? words[hashIndex] : '';
                    const afterHash = hashIndex !== -1 ? words.slice(hashIndex + 1).join(' ') : '';

                    if (['New', 'Edit', 'Add', 'View'].includes(firstToken)) {
                      const rest = currentTitle.substring(firstToken.length + 1);
                      return (
                        <>
                          <Typography component="span" variant="inherit" sx={{ color: 'primary.main' }}>{firstToken}</Typography>{' '}
                          {beforeHash && `${beforeHash} `}
                          {hashPart && (
                            <Typography component="span" variant="inherit" sx={{ color: 'text.secondary' }}>
                              {hashPart}
                            </Typography>
                          )}{' '}
                          {afterHash}
                        </>
                      );
                    }
                    return <FormattedMessage id={currentTitle} />;
                  })()}
                  {/* Show inline tooltip/icon next to title for large log counts */}
                  {(totalRecords !== undefined &&
                    (location.pathname === '/apps/loginLogs' || location.pathname === '/apps/emailLogs' || location.pathname === '/apps/emaillogs') &&
                    totalRecords >= 1000) && (
                      <Tooltip
                        title={
                          <div
                            style={{
                              background: theme.palette.warning.lighter || '#f8f5edff',
                              border: `1px solid ${theme.palette.warning.light || '#F4C96B'}`,
                              borderRadius: '6px',
                              padding: '10px 14px',
                              fontSize: '12px',
                              color: theme.palette.text.primary,
                              lineHeight: '18px',
                            }}
                          >
                            <div
                              style={{
                                fontWeight: '600',
                                marginBottom: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                color: theme.palette.text.primary,
                              }}
                            >
                              <span style={{ color: theme.palette.warning.main }}>⚠</span>
                              <span>Notice: Limited Records Displayed</span>
                            </div>

                            <div>
                              We are currently displaying the first{' '}
                              <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>
                                1000
                              </span>{' '}
                              records out of a total of{' '}
                              <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>
                                {totalRecords}
                              </span>{' '}
                              records.
                            </div>

                            <div>
                              To view the complete data, please narrow down your search criteria.
                            </div>
                          </div>
                        }
                        arrow
                        placement="top"
                        PopperProps={{
                          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }]
                        }}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'white',
                              boxShadow: 3,
                              color: 'gray',
                              fontSize: '0.875rem',
                              borderRadius: 1,
                              maxWidth: 600,
                              padding: 1.5
                            }
                          },
                          arrow: { sx: { color: 'white' } }
                        }}
                      >
                        <IconButton size="small" color="primary" sx={{ ml: 1, verticalAlign: 'middle', display: 'inline-flex' }}>
                          <InfoCircleOutlined style={{ fontSize: '14px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  {recordLimitInfo &&
                    recordLimitInfo.totalRecords > (recordLimitInfo.limit ?? 100) &&
                    recordLimitInfo.totalRecords > (recordLimitInfo.displayedCount ?? 100) &&
                    !recordLimitInfo.showAll ? (
                    <RecordLimitWarning
                      displayedCount={recordLimitInfo.displayedCount ?? 100}
                      totalRecords={recordLimitInfo.totalRecords}
                      loading={recordLimitInfo.isLoading}
                      onShowAll={recordLimitInfo.onShowAll}
                    />
                  ) : (
                    <>
                      {itemSubtitle && (
                        <Typography component="div" variant="subtitle2" color="text.secondary">
                          <FormattedMessage id={itemSubtitle as string} />
                        </Typography>
                      )}
                      {custom && subHeading && (
                        <Typography component="div" variant="subtitle2" color="text.secondary">
                          <FormattedMessage id={subHeading as string} />
                        </Typography>
                      )}
                    </>
                  )}
                </Typography>
              </Grid>
            )}
            <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
              {!window.location.pathname.includes('dashboard') && tempContent}
              {headerAction}
            </Grid>
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  return breadcrumbContent;
}
